import { StartAiDetectionEventProperties } from "@/analytics/ai-detection-events-properties";
import { EventType } from "@/analytics/analytics-events";
import { AI_GRADIENT } from "@/components/common/ai-assets/ai-gradients";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { selectShowStartAssetDetection } from "@/store/ai-asset-review/ai-asset-review-selectors";
import { Features } from "@/store/features/features";
import { selectHasFeature } from "@/store/features/features-slice";
import { useAppSelector } from "@/store/store-hooks";
import { FaroButton, grey, useToast } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { selectProjectId } from "@faro-lotv/project-source";
import { useApiClientContext } from "@faro-lotv/service-wires";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { useCallback, useEffect, useState, type JSX } from "react";

/** @returns button to start AI asset detection when no AI annotations exist */
export function AiStartDetection(): JSX.Element {
  const projectId = useAppSelector(selectProjectId);
  const { aiServicesApiClient } = useApiClientContext();
  const showStartAssetDetection = useAppSelector(selectShowStartAssetDetection);
  const canRerunAssetDetection = useAppSelector(
    selectHasFeature(Features.RerunAssetDetection),
  );
  const [isWaitingForDetectionToStart, setIsWaitingForDetectionToStart] =
    useState(false);
  const [timeoutRef, setTimeoutRef] = useState<NodeJS.Timeout | null>(null);
  const DETECTION_WAIT_TIMEOUT = 10_000;
  const [isStarting, setIsStarting] = useState(false);
  const { handleErrorWithToast } = useErrorHandlers();
  const { openToast } = useToast();

  /** Waiting for detection to start, i.e. background task to be added to the store */
  useEffect(() => {
    if (showStartAssetDetection) {
      setIsWaitingForDetectionToStart(false);
      if (timeoutRef) {
        clearTimeout(timeoutRef);
        setTimeoutRef(null);
      }
    }
  }, [
    showStartAssetDetection,
    timeoutRef,
    setIsWaitingForDetectionToStart,
    setTimeoutRef,
  ]);

  const handleStartDetection = useCallback(async () => {
    if (!projectId) {
      console.error("No project ID available for AI detection");
      return;
    }

    setIsStarting(true);
    try {
      const response = await aiServicesApiClient.triggerObjectDetection(
        canRerunAssetDetection,
      );
      if (response.status !== "success") {
        throw new Error("Failed to start AI detection", {
          cause: response,
        });
      }
      Analytics.track<StartAiDetectionEventProperties>(
        EventType.startAiDetection,
        {
          via: "annotation panel",
        },
      );
      openToast({
        title: "AI detection started",
        message:
          "AI detection is now running. You can follow the progress in the activity panel.",
      });
      setIsWaitingForDetectionToStart(true);
      setTimeoutRef(
        setTimeout(() => {
          setIsWaitingForDetectionToStart(false);
        }, DETECTION_WAIT_TIMEOUT),
      );
    } catch (error) {
      console.error("Failed to start AI detection:", error);
      handleErrorWithToast({
        title: "Failed to start AI detection",
        error,
      });
      setTimeoutRef(null);
      setIsWaitingForDetectionToStart(false);
    } finally {
      setIsStarting(false);
    }
  }, [
    projectId,
    openToast,
    handleErrorWithToast,
    aiServicesApiClient,
    setTimeoutRef,
    setIsWaitingForDetectionToStart,
    canRerunAssetDetection,
  ]);

  return (
    <FaroButton
      variant="primary"
      onClick={handleStartDetection}
      size="m"
      disabled={isStarting || isWaitingForDetectionToStart}
      icon={<AutoFixHighIcon sx={{ width: 16, height: 16 }} />}
      sx={{
        ...AI_GRADIENT,
        color: grey[0],
      }}
    >
      {isStarting || isWaitingForDetectionToStart
        ? "Starting detection..."
        : "Start AI asset detection"}
    </FaroButton>
  );
}
